import React, { useRef } from 'react'
import toNumber from 'lodash/toNumber'
import { InlineDisplayContent, NumberInput } from '@ztechapps/webui.input'
import { ShowIf } from '@ztechapps/webui.util'
import { formatMoney } from '@ztechapps/common.utils'
import { useConfirmDialog } from '@ztechapps/common.hook'
import {
  computeDefaultInsuredAmount,
  isInsuredAmountAcknowledgementGated,
  isInsuredAmountBelowDefault,
  MINIMUM_INSURED_AMOUNT,
} from '@ztechapps/order.common'
import { useCheckoutContext } from '../../index'

export const InsuredAmount = () => {
  const context = useCheckoutContext()

  const [shippingOptions, setShippingOptions] = context.shippingOptions

  const defaultAmount = computeDefaultInsuredAmount(shippingOptions?.gross)
  const isBelowDefault = isInsuredAmountBelowDefault(shippingOptions?.insuredAmount, shippingOptions?.gross)
  const isAcknowledgementGated = isInsuredAmountAcknowledgementGated(
    shippingOptions?.insuredAmount,
    shippingOptions?.gross,
    shippingOptions?.hasAcknowledgedLowInsuredAmount
  )

  // The dialog returns focus to the field on close, which re-arms the wrapping onBlur below.
  // Held true for the duration of the confirm/cancel round-trip so that re-entrant blur can't
  // open a second dialog.
  const isDialogOpenRef = useRef(false)

  // Remembers the value the shopper already declined so that the blur re-fired by the dialog's
  // own focus-restore doesn't reopen it for the same value. Cleared on confirm and whenever the
  // shopper types a new value, so a genuinely changed amount is still evaluated.
  const declinedAmountRef = useRef<number | undefined>(undefined)

  const { confirm } = useConfirmDialog({
    title: 'Use a lower declared value?',
    text: `You've declared less than your order value. If the shipment is lost or damaged, the carrier's payout may not cover the full ${formatMoney(defaultAmount)}.`,
    buttons: { confirm: { text: 'Yes, use this declared value' } },
  })

  return (
    <>
      <div
        onBlur={async (e) => {
          //NumberInput corrects the DOM display below its own min but, being controlled, discards that
          //correction on the next render and never reports it back through onChange. React's blur bubbles
          //and NumberInput doesn't stop propagation, so by the time this handler runs the input already
          //holds the settled (clamped) value -- read it here and merge it in the same shape as onChange.
          const value = toNumber((e.target as HTMLInputElement)?.value)

          if (isDialogOpenRef.current || declinedAmountRef.current === value) {
            return
          }

          if (
            isInsuredAmountBelowDefault(value, shippingOptions?.gross) &&
            !shippingOptions?.hasAcknowledgedLowInsuredAmount
          ) {
            isDialogOpenRef.current = true
            await confirm(
              async () => {
                declinedAmountRef.current = undefined
                setShippingOptions({ insuredAmount: value, hasAcknowledgedLowInsuredAmount: true })
                isDialogOpenRef.current = false
              },
              async () => {
                // Cancel keeps the shopper's typed value, remembers it was declined so the next
                // blur of the same value doesn't reopen the dialog, and leaves the step blocked.
                declinedAmountRef.current = value
                setShippingOptions({ insuredAmount: value, hasAcknowledgedLowInsuredAmount: false })
                isDialogOpenRef.current = false
              }
            )
          } else if (value !== shippingOptions?.insuredAmount) {
            setShippingOptions({ insuredAmount: value })
          }
        }}
      >
        <InlineDisplayContent
          label="Declared value; minimum of $100"
          help="Declared value for the insurance. Minimum is $100"
        >
          <NumberInput
            min={MINIMUM_INSURED_AMOUNT}
            placeholder="Set the declared value for this shipping."
            value={shippingOptions?.insuredAmount}
            onChange={(e) => {
              declinedAmountRef.current = undefined
              setShippingOptions({ insuredAmount: toNumber(e?.target.value), hasAcknowledgedLowInsuredAmount: false })
            }}
          />
        </InlineDisplayContent>
      </div>
      <ShowIf condition={isBelowDefault}>
        {/* Contrast record for this pairing: libs/webui/input/src/InlineInput/HelpBlockLayout.tsx:26-28. */}
        <div className="small text-danger-emphasis">
          {`You've declared less than your order value. If the shipment is lost or damaged, the carrier's payout may not cover the full ${formatMoney(defaultAmount)}.`}
        </div>
      </ShowIf>
      <ShowIf condition={isAcknowledgementGated}>
        <div className="small text-danger-emphasis">Confirm this declared value before you can continue.</div>
      </ShowIf>
    </>
  )
}
